import { createContext, useContext, type ReactNode, type CSSProperties } from 'react';

interface ListGridCtx {
  columns: string;
}

const ListGridContext = createContext<ListGridCtx | null>(null);

function useListGrid(): ListGridCtx {
  const ctx = useContext(ListGridContext);
  if (!ctx) throw new Error('ListGrid.Header/Row must be used inside <ListGrid>');
  return ctx;
}

interface ListGridProps {
  columns: string;
  ariaLabel: string;
  className?: string;
  children: ReactNode;
}

export function ListGrid({ columns, ariaLabel, className = '', children }: ListGridProps) {
  return (
    <div
      role="grid"
      aria-label={ariaLabel}
      className={`bg-white border border-slate-200 rounded-xl overflow-hidden ${className}`}
    >
      <ListGridContext.Provider value={{ columns }}>{children}</ListGridContext.Provider>
    </div>
  );
}

interface HeaderProps {
  children: ReactNode;
  className?: string;
  style?: CSSProperties;
}

function Header({ children, className = '', style }: HeaderProps) {
  const { columns } = useListGrid();
  return (
    <div
      role="row"
      style={{ gridTemplateColumns: columns, ...style }}
      className={`grid border-b border-slate-200 bg-slate-50 font-mono-num text-[10.5px] font-semibold uppercase tracking-[0.14em] text-slate-500 [&>*]:px-3 [&>*]:py-2.5 ${className}`}
    >
      {children}
    </div>
  );
}

interface RowProps {
  children: ReactNode;
  onClick?: () => void;
  className?: string;
  style?: CSSProperties;
}

function Row({ children, onClick, className = '', style }: RowProps) {
  const { columns } = useListGrid();
  const interactive = !!onClick;
  return (
    <div
      role="row"
      style={{ gridTemplateColumns: columns, ...style }}
      onClick={onClick}
      onKeyDown={
        interactive
          ? (e) => {
              if (e.key === 'Enter' || e.key === ' ') {
                e.preventDefault();
                onClick?.();
              }
            }
          : undefined
      }
      tabIndex={interactive ? 0 : -1}
      className={`grid items-stretch text-[13px] border-t border-slate-100 first:border-t-0 transition-colors ${
        interactive ? 'hover:bg-slate-50 cursor-pointer group focus:outline-none focus:ring-2 focus:ring-[#00A871]/40 focus:ring-inset' : ''
      } ${className}`}
    >
      {children}
    </div>
  );
}

ListGrid.Header = Header;
ListGrid.Row = Row;
